import type { LoadingFailure } from "./loading-state.ts";

/**
 * What a notice's button does with the failure shown (P1.4-T5, P1.9-T15, ADR-078). Retry, before the reveal, asks
 * the scene to retry what failed. After the page left a world it showed, the disconnect notice's Reconnect joins
 * again through the loading shell, reusing the loaded world. Log in, for an account whose sign-in was refused or
 * ended, before the reveal or after it, forgets the tab's sign-in and reloads onto the log in form. Reload reloads
 * the page, after the reveal or before it, where only a reload mends the failure (a stale page, a newer world
 * version). View status, for maintenance's close (4011), does what Log in does: a tab that kept its grant would take
 * the stored session's fast path back to a join the gateway refuses with 4011 again, so it forgets the grant and the
 * reload opens on the login flow, whose first answer from the server shows the maintenance page.
 */

/**
 * The page's ways to act: reload it, forget the tab's sign-in and reload (the notice's Log in), and retry in place,
 * null before the scene exists, when a retry reloads instead.
 */
export interface NoticeHandlers {
  readonly reload: () => void;
  readonly logIn: () => void;
  readonly retry: (() => void) | null;
}

/**
 * Acts on a notice's button.
 *
 * @param action The button's action.
 * @param handlers The page's ways to act.
 * @example
 * actOnNotice("status", { reload: () => location.reload(), logIn, retry: null });
 */
export function actOnNotice(action: LoadingFailure["action"], handlers: NoticeHandlers): void {
  switch (action) {
    case "reload":
      handlers.reload();
      return;
    case "log-in":
    case "status":
      handlers.logIn();
      return;
    case "reconnect":
    case "retry":
      if (handlers.retry === null) {
        handlers.reload();
        return;
      }
      handlers.retry();
  }
}
